import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  DeleteSweep,
  FiberManualRecord,
} from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import MessageCard from './MessageCard';
import ForwardDialog from './ForwardDialog';
import { trackButtonClick, trackMessageAction, trackExternalMessage } from '../utils/analytics';
import { localStorageService } from '../services/LocalStorageService';
import { secureStorage } from '../services/SecureStorageService';
import API_CONFIG from '../config/api.config';
import { Message } from '../services/api';

interface ReceivePageProps {
  onBack: () => void;
  onReceiveByCode?: () => void;
}

const ReceivePage: React.FC<ReceivePageProps> = observer(({ onBack, onReceiveByCode }) => {
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [messageToForward, setMessageToForward] = useState<Message | null>(null);

  // Load messages for current device when page opens
  useEffect(() => {
    if (appStore.currentDevice) {
      appStore.loadMessages(appStore.currentDevice.id, true);
      // Mark all messages as read in local storage when opening receive page
      // The backend marks them as read, but we also update local storage
      localStorageService.markAllMessagesAsRead(appStore.currentDevice.id).catch(err => {
        console.error('Failed to mark messages as read in local storage:', err);
      });
    }
  }, []);

  const handleClearAll = async () => {
    trackButtonClick('Confirm Clear All', 'Receive Page Dialog');
    trackMessageAction('clear_all_confirmed', 'messages');
    await appStore.clearAllMessages();
    setShowClearAllDialog(false);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      appStore.showToast('Copied to clipboard');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      appStore.showToast('Failed to copy to clipboard');
    }
  };

  const handleOpenForwardDialog = (message: Message) => {
    trackButtonClick('Forward Message', 'Receive Page');
    trackMessageAction('forward_initiate', message.type);
    setMessageToForward(message);
    setForwardDialogOpen(true);
  };

  const handleCloseForwardDialog = () => {
    setForwardDialogOpen(false);
    setMessageToForward(null);
  };

  const handleForwardToDevice = async (device: any) => {
    if (messageToForward) {
      try {
        await appStore.forwardMessage(messageToForward, device);
        // Close dialog after successful forward
        handleCloseForwardDialog();
      } catch (error) {
        console.error('Failed to forward message:', error);
        // Error is already handled in AppStore, but keep dialog open so user can try again
      }
    } else {
      console.error('No message to forward');
      appStore.setError('No message selected for forwarding');
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        py: 1,
        px: 1,
        maxWidth: 1200,
        mx: 'auto',
        width: '100%',
      }}
    >
      {/* Header */}
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)', mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 0.5 }}>
                Received messages
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FiberManualRecord
                  sx={{
                    fontSize: 12,
                    color: appStore.serverConnected ? '#4CAF50' : '#F44336',
                  }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                  {appStore.serverConnected ? 'Connected to service' : 'Offline'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {onReceiveByCode && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    trackButtonClick('Receive by Code', 'Receive Page');
                    trackExternalMessage('initiate', 'receive');
                    onReceiveByCode();
                  }}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#FFFFFF',
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Receive by Code
                </Button>
              )}
              {appStore.messages.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweep />}
                  onClick={() => {
                    trackButtonClick('Clear All Messages', 'Receive Page');
                    trackMessageAction('clear_all', 'messages');
                    setShowClearAllDialog(true);
                  }}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#FFFFFF',
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {appStore.isLoadingMessages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        ) : appStore.messages.length === 0 ? (
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textAlign: 'center',
                  py: 2,
                  fontSize: '1rem',
                  lineHeight: 1.6,
                }}
              >
                No messages received yet.
                <br />
                <br />
                Messages sent to this device will be stored locally and appear here.
              </Typography>
            </CardContent>
          </Card>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {appStore.messages.map((message, index) => (
              <MessageCard
                key={`${message.contentId}-${index}`}
                message={message}
                onCopy={() => message.text && copyToClipboard(message.text)}
                onDelete={() => appStore.deleteMessage(message.contentId)}
                onForward={() => handleOpenForwardDialog(message)}
                showDeleteButton={true}
                showForwardButton={true}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={showClearAllDialog}
        onClose={() => setShowClearAllDialog(false)}
      >
        <DialogTitle sx={{ color: '#FFFFFF' }}>Clear All Messages</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Are you sure you want to delete all messages? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              trackButtonClick('Cancel Clear All', 'Receive Page Dialog');
              setShowClearAllDialog(false);
            }}
            sx={{ color: '#FFFFFF' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearAll}
            variant="contained"
            color="error"
            sx={{
              backgroundColor: '#F44336',
              '&:hover': {
                backgroundColor: '#D32F2F',
              },
            }}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forward Dialog */}
      <ForwardDialog
        open={forwardDialogOpen}
        onClose={handleCloseForwardDialog}
        devices={appStore.devices}
        currentDeviceId={secureStorage.getDeviceId() || ''}
        onSelectDevice={handleForwardToDevice}
      />
    </Box>
  );
});

export default ReceivePage;

